import React, { useState } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Grid,
  Container,
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  Collapse,
  IconButton,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddNewUserToggle from "./AddNewUserToggle";

const FilterAndClearUsers = ({ fetchUsers, setFilters, filters }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [showFilters, setShowFilters] = useState(false); // hidden by default

  const handleChange = (e) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [e.target.name]: e.target.value,
    }));
  };

  const handleApplyFilters = () => {
    fetchUsers(filters);
  };

  const handleClear = () => {
    const resetFilters = { role: "", username: "", email: "", sort: "DESC" };
    setFilters(resetFilters);
    fetchUsers(resetFilters);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {/* Header with toggle icon */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h4" fontWeight={600} color="primary" fontFamily= "Roboto, sans-serif" >
          Filter Users
        </Typography>
        <IconButton
          onClick={() => setShowFilters((prev) => !prev)}
          color="primary"
          sx={{
            backgroundColor: "#e3f2fd",
            "&:hover": { backgroundColor: "#bbdefb" },
          }}
        >
          <FilterListIcon />
        </IconButton>
      </Box>

      {/* Collapsible Filter Section */}
      <Collapse in={showFilters}>
        <Paper
          sx={{
            padding: 4,
            marginBottom: 4,
            borderRadius: 3,
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            border: "1px solid rgba(0,0,0,0.05)",
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                size="small"
                value={filters.email}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "rgba(0,0,0,0.1)" },
                    "&:hover fieldset": { borderColor: "#3f51b5" },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#5c6bc0",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                size="small"
                value={filters.username}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "rgba(0,0,0,0.1)" },
                    "&:hover fieldset": { borderColor: "#3f51b5" },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#5c6bc0",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: "#5c6bc0" }}>Role</InputLabel>
                <Select
                  name="role"
                  value={filters.role}
                  onChange={handleChange}
                  sx={{
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0,0,0,0.1)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3f51b5",
                    },
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="data-admin">Data-Admin</MenuItem>
                  <MenuItem value="super-admin">Super-Admin</MenuItem>
                  <MenuItem value="web-admin">Web-Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Filter Buttons */}
          <Box
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              flexDirection: isSmallScreen ? "column" : "row",
            }}
          >
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                background: "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)",
                boxShadow: "0 2px 10px rgba(76, 175, 80, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #43a047 0%, #5cb860 100%)",
                  boxShadow: "0 4px 14px rgba(76, 175, 80, 0.4)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Apply Filters
            </Button>

            <Button
              variant="contained"
              onClick={handleClear}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                background: "linear-gradient(135deg, #f44336 0%, #ef5350 100%)",
                boxShadow: "0 2px 10px rgba(244, 67, 54, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #e53935 0%, #e57373 100%)",
                  boxShadow: "0 4px 14px rgba(244, 67, 54, 0.4)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Add New User Component */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 4 }}>
        <AddNewUserToggle />
      </Box>
    </Container>
  );
};

export default FilterAndClearUsers;
